
import React from 'react';
import { Star, Trash2, ChevronUp, ChevronDown, Pencil } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Task as TaskType, WalletSettings } from '@/utils/storage';
import { Card, CardContent } from '@/components/ui/card';
import TaskCountdown from './TaskCountdown';

interface TaskProps {
  task: TaskType;
  onComplete: (task: TaskType) => void;
  onEdit?: (task: TaskType) => void;
  onDelete?: (taskId: string) => void;
  onMoveUp?: (taskId: string) => void;
  onMoveDown?: (taskId: string) => void;
  onUpdateStars?: (taskId: string, change: number) => void;
  onTaskReset?: (task: TaskType) => void;
  editMode?: boolean;
  isAnimating?: boolean;
  walletSettings?: WalletSettings;
}

const Task: React.FC<TaskProps> = ({
  task,
  onComplete,
  onEdit,
  onDelete,
  onMoveUp,
  onMoveDown,
  onUpdateStars,
  onTaskReset,
  editMode = false,
  isAnimating = false,
  walletSettings
}) => {
  const handleCompleteTask = (e: React.MouseEvent) => {
    e.preventDefault();
    if (!task.completed && !isAnimating) {
      onComplete(task);
    }
  };
  
  const handleUpdateStars = (change: number, e: React.MouseEvent) => {
    e.stopPropagation();
    if (onUpdateStars) {
      onUpdateStars(task.id, change);
    }
  };
  
  // Calculate dollar value for this task's stars when wallet is enabled
  const dollarValue = walletSettings?.enabled && walletSettings?.conversionRate 
    ? (task.stars / walletSettings.conversionRate).toFixed(2)
    : null;
  
  return (
    <Card 
      className={cn(
        "relative transition-all duration-200 hover:shadow-md border-border/50",
        task.completed ? "opacity-60 bg-opacity-70" : "opacity-100",
        !editMode && !task.completed ? "cursor-pointer" : "cursor-default"
      )}
      onClick={editMode ? undefined : handleCompleteTask}
    >
      <CardContent className="p-4">
        <div className="flex justify-between items-center">
          <div className="flex-1">
            <h3 className={cn(
              "font-medium text-lg flex items-center",
              task.completed && "line-through text-gray-500"
            )}>
              {task.title}
            </h3>
            
            {task.description && (
              <p className="text-sm text-muted-foreground mt-1">
                {task.description}
              </p>
            )}
            
            {task.resetInterval > 0 && (
              <>
                <p className="text-xs text-muted-foreground mt-1">
                  Resets {getResetIntervalLabel(task.resetInterval)}
                </p>
                <TaskCountdown task={task} onTaskReset={onTaskReset} />
              </>
            )}
            
            {task.daily && (
              <p className="text-xs text-muted-foreground mt-1">
                Daily task
              </p>
            )}
          </div>
          
          <div className="flex items-center gap-2">
            {editMode ? (
              <div className="flex items-center">
                <button 
                  onClick={(e) => handleUpdateStars(-1, e)}
                  className="bg-gray-100 dark:bg-gray-600 hover:bg-gray-200 dark:hover:bg-gray-500 rounded-full w-8 h-8 flex items-center justify-center text-lg"
                  aria-label="Decrease stars"
                >
                  -
                </button>
                <div id={`task-${task.id}`} className="flex items-center justify-center mx-1 relative w-14">
                  <div className="flex flex-col items-center">
                    <Star size={24} className="text-yellow-400 fill-yellow-400" />
                    {dollarValue && (
                      <span className="text-xs text-muted-foreground mt-1">
                        ${dollarValue}
                      </span>
                    )}
                  </div>
                  <span className="ml-1 font-medium">{task.stars}</span>
                </div>
                <button 
                  onClick={(e) => handleUpdateStars(1, e)}
                  className="bg-gray-100 dark:bg-gray-600 hover:bg-gray-200 dark:hover:bg-gray-500 rounded-full w-8 h-8 flex items-center justify-center text-lg"
                  aria-label="Increase stars"
                >
                  +
                </button>
              </div>
            ) : (
              <div id={`task-${task.id}`} className="flex items-center">
                <div className="flex flex-col items-center">
                  <Star size={28} className="text-yellow-400 fill-yellow-400" />
                  {dollarValue && (
                    <span className="text-xs text-muted-foreground mt-1">
                      ${dollarValue}
                    </span>
                  )}
                </div>
                <span className="ml-1 font-medium text-lg">{task.stars}</span>
              </div>
            )}
          </div>
        </div>
        
        {editMode && (
          <div className="flex justify-between mt-3">
            <div className="flex space-x-2">
              <button 
                onClick={() => onMoveUp && onMoveUp(task.id)}
                className="p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
                aria-label="Move task up"
              >
                <ChevronUp size={18} />
              </button>
              <button 
                onClick={() => onMoveDown && onMoveDown(task.id)}
                className="p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
                aria-label="Move task down"
              >
                <ChevronDown size={18} />
              </button>
            </div>
            
            <div className="flex space-x-2">
              <button 
                onClick={() => onEdit && onEdit(task)}
                className="p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <Pencil size={18} className="text-primary" />
              </button>
              
              <button 
                onClick={() => onDelete && onDelete(task.id)}
                className="p-1.5 rounded-full hover:bg-red-100 dark:hover:bg-red-900/50 text-red-500"
                aria-label="Delete task"
              >
                <Trash2 size={18} />
              </button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

function getResetIntervalLabel(minutes: number): string {
  if (minutes < 60) return `every ${minutes} minutes`;
  if (minutes === 60) return 'every hour';
  if (minutes < 1440) return `every ${minutes / 60} hours`;
  if (minutes === 1440) return 'every day';
  return `every ${minutes / 1440} days`;
}

export default Task;
